import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, onSnapshot, query, orderBy, doc, setDoc, serverTimestamp,
  deleteDoc, updateDoc, where, getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { AY_ADLARI, paraCevir, ayKey } from '../utils/takvim';
import {
  Wallet, TrendingUp, AlertCircle, Users, ArrowRight,
  Plus, X, Receipt, Filter, CheckCircle2, ChevronRight,
  CreditCard, Search, Calendar, ChevronDown, ChevronUp,
  Trash2, History, BarChart3, ArrowUpDown, Eye, EyeOff,
  FileText, Calculator, ArrowDown, ArrowUp, CircleDollarSign,
  PiggyBank, BadgeDollarSign, Clock, MoreHorizontal, XCircle,
  Download
} from 'lucide-react';
import html2pdf from 'html2pdf.js';

// ═══════════════════════════════════════════════
// PDF MAKBUZ OLUSTURUCU (jsPDF)
// ═══════════════════════════════════════════════

const makbuzOlustur = (odeme, usta, aylikOzet) => {
  // html2pdf kütüphanesini dinamik olarak içe aktarıyoruz
  import('html2pdf.js').then((module) => {
    const html2pdf = module.default || module;
    
    // Tarih ve zaman değişkenleri
    const odemeTarihi = odeme.tarih?.toDate ? odeme.tarih.toDate() : new Date(odeme.tarih);
    const tarihStr = odemeTarihi.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
    const saatStr = odemeTarihi.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    const gunStr = odemeTarihi.toLocaleDateString('tr-TR', { weekday: 'long' });

    // Dinamik renkler ve metinler
    const yontemRenk = odeme.odemeYontemi === 'nakit' ? '#28a745' : odeme.odemeYontemi === 'havale' ? '#0d6efd' : '#d98300';
    const yontemText = odeme.odemeYontemi === 'nakit' ? 'NAKİT' : odeme.odemeYontemi === 'havale' ? 'HAVALE / EFT' : 'ÇEK';

    // Hesap detayları
    const ilgiliAy = odeme.ilgiliAy || '---';
    const ayVerisi = aylikOzet?.[ilgiliAy];
    const oncekiBorc = (ayVerisi?.hakedis || 0) - (ayVerisi?.avans || 0) - (ayVerisi?.odeme || 0) + odeme.tutar;
    const kalanBorc = (ayVerisi?.kalanBorc || 0);

    // --- HTML ŞABLONU OLUŞTURMA ---
    // CSS flexbox ve tablolar kullanarak hizalama sorunlarını kökten çözüyoruz.
    const container = document.createElement('div');
    container.innerHTML = `
      <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #333; padding: 20px; width: 800px; margin: 0 auto; box-sizing: border-box; line-height: 1.4;">
        
        <div style="background-color: #1a1a1a; color: #fff; padding: 20px 25px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <div>
            <h1 style="margin: 0; font-size: 28px; font-weight: bold; letter-spacing: 1px;">AGE</h1>
            <div style="font-size: 10px; color: #b0b0b0; font-weight: bold; margin-top: 5px;">İZOLASYON VE ÇATI SİSTEMLERİ</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 13px; font-weight: bold; color: #b0b0b0;">ÖDEME DEKONTU</div>
          </div>
        </div>

        <div style="background-color: #f8f9fa; border: 1px solid #f0f0f0; padding: 15px 20px; display: flex; justify-content: space-between; margin-bottom: 20px;">
          <div style="flex: 1;">
            <div style="font-size: 10px; font-weight: bold; color: #777; text-transform: uppercase; margin-bottom: 4px;">Dekont Tarihi</div>
            <div style="font-size: 14px; font-weight: bold; color: #1a1a1a;">${tarihStr} - ${gunStr}</div>
            <div style="font-size: 11px; color: #666; margin-top: 2px;">Saat: ${saatStr}</div>
          </div>
          <div style="flex: 1;">
            <div style="font-size: 10px; font-weight: bold; color: #777; text-transform: uppercase; margin-bottom: 4px;">Dekont No</div>
            <div style="font-size: 14px; font-weight: bold; color: #1a1a1a;">#${odeme.id?.slice(-8).toUpperCase() || '--------'}</div>
          </div>
          <div style="flex: 1; text-align: right;">
            <div style="font-size: 10px; font-weight: bold; color: #777; text-transform: uppercase; margin-bottom: 4px;">İşlem Tipi</div>
            <div style="font-size: 14px; font-weight: bold; color: ${yontemRenk};">${yontemText}</div>
          </div>
        </div>

        <div style="border: 1px solid #e0e0e0; display: flex; margin-bottom: 25px;">
          <div style="flex: 1.2; padding: 15px 20px; border-right: 1px solid #e0e0e0;">
            <div style="font-size: 10px; font-weight: bold; color: #777; text-transform: uppercase; margin-bottom: 8px;">Ödenen Tutar</div>
            <div style="font-size: 26px; font-weight: bold; color: ${odeme.fazlaOdeme ? '#0d6efd' : '#28a745'};">${paraCevir(odeme.tutar)}</div>
            ${odeme.fazlaOdeme ? `<div style="font-size: 10px; font-weight: bold; color: #0d6efd; margin-top: 4px;">(FAZLA ÖDEME / AVANS)</div>` : ''}
          </div>
          <div style="flex: 1; padding: 15px 20px; border-right: 1px solid #e0e0e0;">
            <div style="font-size: 10px; font-weight: bold; color: #777; text-transform: uppercase; margin-bottom: 8px;">Ödeme Yapan</div>
            <div style="font-size: 13px; font-weight: bold; color: #1a1a1a; margin-bottom: 4px;">AGE İzolasyon ve Çatı Sistemleri</div>
            <div style="font-size: 11px; color: #555;">İbrahim Öztin</div>
            <div style="font-size: 11px; color: #555;">Sancaktepe / İstanbul</div>
            <div style="font-size: 11px; color: #555;">+90 (507) 855 52 15</div>
          </div>
          <div style="flex: 1; padding: 15px 20px; text-align: right;">
            <div style="font-size: 10px; font-weight: bold; color: #777; text-transform: uppercase; margin-bottom: 8px;">Ödeme Yapılan</div>
            <div style="font-size: 13px; font-weight: bold; color: #1a1a1a; margin-bottom: 4px;">${usta?.ad || 'Bilinmeyen'}</div>
          </div>
        </div>

        <div style="background-color: #f8f9fa; border: 1px solid #e0e0e0; margin-bottom: 25px;">
          <div style="font-size: 12px; font-weight: bold; color: #1a1a1a; padding: 12px 20px; border-bottom: 1px solid #dcdcdc;">HESAP DETAYI</div>
          <div style="padding: 15px 20px; display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0;">
            <div style="flex: 1;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">İLGİLİ DÖNEM</div>
              <div style="font-size: 13px; font-weight: bold; color: #1a1a1a;">${ilgiliAy}</div>
            </div>
            <div style="flex: 1;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">HAKEDİŞ</div>
              <div style="font-size: 13px; font-weight: bold; color: #28a745;">${paraCevir(ayVerisi?.hakedis || 0)}</div>
            </div>
            <div style="flex: 1;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">AVANS</div>
              <div style="font-size: 13px; font-weight: bold; color: #ffc107;">${paraCevir(ayVerisi?.avans || 0)}</div>
            </div>
            <div style="flex: 1; text-align: right;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">ÖNCEKİ BAKİYE</div>
              <div style="font-size: 13px; font-weight: bold; color: #dc3545;">${paraCevir(Math.max(0, oncekiBorc))}</div>
            </div>
          </div>
          <div style="padding: 15px 20px; display: flex; justify-content: space-between;">
            <div style="flex: 1;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">BU ÖDEME</div>
              <div style="font-size: 16px; font-weight: bold; color: #28a745;">- ${paraCevir(odeme.tutar)}</div>
            </div>
            <div style="flex: 1; text-align: right;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">KALAN BORÇ</div>
              <div style="font-size: 16px; font-weight: bold; color: ${kalanBorc > 0 ? '#dc3545' : '#28a745'};">
                ${kalanBorc > 0 ? paraCevir(kalanBorc) : 'KAPANDI ✓'}
              </div>
            </div>
          </div>
          ${odeme.kesimNoktasi ? `<div style="font-size: 11px; font-weight: bold; color: #28a745; padding: 0 20px 15px 20px;">* Bu ödeme ile ilgili ay tamamen kapatılmıştır.</div>` : ''}
        </div>

        <div style="border: 1px solid #e0e0e0; margin-bottom: 25px;">
          <div style="font-size: 12px; font-weight: bold; color: #1a1a1a; padding: 12px 20px; border-bottom: 1px solid #dcdcdc;">ÖDEME YÖNTEMİ DETAYI</div>
          <div style="padding: 15px 20px; display: flex;">
            <div style="flex: 1;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">YÖNTEM</div>
              <div style="font-size: 13px; font-weight: bold; color: #1a1a1a;">${yontemText}</div>
            </div>
            ${odeme.odemeYontemi === 'havale' && odeme.ibanBilgisi ? `
            <div style="flex: 2;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">GÖNDEREN HESAP (IBAN)</div>
              <div style="font-size: 12px; font-weight: bold; color: #1a1a1a;">${odeme.ibanBilgisi.iban || '---'}</div>
              <div style="font-size: 11px; color: #666; margin-top: 2px;">Hesap Sahibi: ${odeme.ibanBilgisi?.hesapSahibi || 'Bilinmiyor'}</div>
              <div style="font-size: 11px; color: #666;">Banka: ${odeme.ibanBilgisi.banka || '---'}</div>
            </div>
            <div style="flex: 1.5;">
               <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">ALICI HESAP</div>
               <div style="font-size: 12px; font-weight: bold; color: #1a1a1a;">${usta?.ad || '---'}</div>
               <div style="font-size: 11px; color: #666; margin-top: 2px;">IBAN: ${odeme.aliciIban || 'Belirtilmedi'}</div>
            </div>` : ''}
            ${odeme.odemeYontemi === 'cek' ? `
            <div style="flex: 1;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">ÇEK NUMARASI</div>
              <div style="font-size: 13px; font-weight: bold; color: #1a1a1a;">${odeme.cekNo || '---'}</div>
            </div>
            <div style="flex: 1;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">ÇEK TARİHİ</div>
              <div style="font-size: 13px; font-weight: bold; color: #1a1a1a;">${odeme.cekTarihi || '---'}</div>
            </div>` : ''}
            ${odeme.odemeYontemi === 'nakit' ? `
            <div style="flex: 2;">
              <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 4px;">ÖDEME YERİ</div>
              <div style="font-size: 13px; font-weight: bold; color: #1a1a1a;">${odeme.odemeYeri || 'Şantiye / Ofis'}</div>
            </div>` : ''}
          </div>
        </div>

        ${odeme.aciklama ? `
        <div style="border: 1px solid #e0e0e0; margin-bottom: 25px;">
          <div style="font-size: 12px; font-weight: bold; color: #1a1a1a; padding: 12px 20px; border-bottom: 1px solid #dcdcdc;">AÇIKLAMA</div>
          <div style="padding: 15px 20px; font-size: 12px; color: #333;">${odeme.aciklama}</div>
        </div>` : ''}

        <div style="display: flex; justify-content: space-between; margin-bottom: 40px; margin-top: 10px;">
          <div style="width: 48%; border: 1px solid #e0e0e0; padding: 15px 20px; height: 100px; box-sizing: border-box;">
            <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 8px;">ÖDEMEYİ YAPAN</div>
            <div style="font-size: 12px; font-weight: bold; color: #1a1a1a;">AGE İzolasyon ve Çatı Sistemleri</div>
            <div style="font-size: 11px; color: #666; margin-top: 4px;">${odeme.ibanBilgisi?.hesapSahibi || '---'}</div>
          </div>
          <div style="width: 48%; border: 1px solid #e0e0e0; padding: 15px 20px; height: 100px; box-sizing: border-box; position: relative;">
            <div style="font-size: 10px; font-weight: bold; color: #777; margin-bottom: 8px;">ÖDEMEYİ ALAN</div>
            <div style="font-size: 12px; font-weight: bold; color: #1a1a1a;">${usta?.ad || '---'}</div>
            <div style="font-size: 11px; color: #999; position: absolute; bottom: 15px;">İmza / Kaşe</div>
          </div>
        </div>

        <div style="border-top: 1px solid #e0e0e0; padding-top: 15px; text-align: center;">
          <div style="font-size: 9px; color: #777; margin-bottom: 4px;">AGE İzolasyon ve Çatı Sistemleri - Sancaktepe / İstanbul - +90 (507) 855 52 15 - ageizolasyon.com</div>
          <div style="font-size: 9px; color: #aaa;">Bu belge elektronik ortamda oluşturulmuştur. </div>
        </div>

      </div>
    `;

    // html2pdf Ayarları
    const secenekler = {
      margin: 0,
      filename: `AGE_Makbuz_${(usta?.ad?.replace(/\s+/g, '_') || 'Odeme')}_${odemeTarihi.toISOString().slice(0, 10)}_#${odeme.id?.slice(-8).toUpperCase() || '--------'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    // PDF'i oluştur ve indir
    html2pdf().set(secenekler).from(container).save();
  });}
  // ═══════════════════════════════════════════════
  // ANA BILEŞEN
  // ═══════════════════════════════════════════════

  export default function Muhasebe() {
    const navigate = useNavigate();
    const bugun = new Date();

    // ─── STATE ─────────────────────────────────────────────────────────────
    const [ustalar, setUstalar] = useState([]);
    const [aliciIban, setAliciIban] = useState('');
    const [santiyeler, setSantiyeler] = useState([]);
    const [calismaVerisi, setCalismaVerisi] = useState({});
    const [avansVerisi, setAvansVerisi] = useState({});
    const [odemeler, setOdemeler] = useState([]);
    const [seciliAy, setSeciliAy] = useState(ayKey(bugun.getFullYear(), bugun.getMonth()));
    const [aramaMetni, setAramaMetni] = useState('');
    const [siralama, setSiralama] = useState('borcAzalan');
    const [detayliGorunum, setDetayliGorunum] = useState(true);
    const [seciliUstaDetay, setSeciliUstaDetay] = useState(null);

    // Odeme Modal State
    const [odemeModalAcik, setOdemeModalAcik] = useState(false);
    const [seciliUsta, setSeciliUsta] = useState(null);
    const [odemeTutari, setOdemeTutari] = useState('');
    const [odemeAciklama, setOdemeAciklama] = useState('');
    const [odemeTarihi, setOdemeTarihi] = useState(bugun.toISOString().split('T')[0]);
    const [odemeYontemi, setOdemeYontemi] = useState('nakit');
    const [yukleniyor, setYukleniyor] = useState(false);

    // Havale detay state'leri
    const [seciliIban, setSeciliIban] = useState('');
    const [havaleAdSoyad, setHavaleAdSoyad] = useState('');
    const [havaleBanka, setHavaleBanka] = useState('');
    const [ibanListesi] = useState([
      { id: '1', iban: 'TR70 0006 2000 7800 0006 5423 82', hesapSahibi: 'Efe Öztin', banka: 'Türkiye Garanti Bankası' },
      { id: '2', iban: 'TR00 9876 5432 1098 7654 3210 98', hesapSahibi: 'Ibrahim Oztin', banka: 'Is Bankasi' },
    ]);

    // Cek detay state'leri
    const [cekNo, setCekNo] = useState('');
    const [cekTarihi, setCekTarihi] = useState(bugun.toISOString().split('T')[0]);

    // Silme onay modalı
    const [silmeModalAcik, setSilmeModalAcik] = useState(false);
    const [silinecekOdeme, setSilinecekOdeme] = useState(null);

    // ─── VERI CEKIMI ───────────────────────────────────────────────────────
    useEffect(() => {
      const unsubUstalar = onSnapshot(
        query(collection(db, 'ustalar'), orderBy('ad', 'asc')),
        snap => setUstalar(snap.docs.map(d => ({ id: d.id, ...d.data() })))
      );
      const unsubSantiyeler = onSnapshot(collection(db, 'santiyeler'), snap =>
        setSantiyeler(snap.docs.map(d => ({ id: d.id, ...d.data() })))
      );
      return () => { unsubUstalar(); unsubSantiyeler(); };
    }, []);

    useEffect(() => {
      const aylar = [];
      for (let i = -22; i <= 2; i++) {
        const d = new Date(bugun.getFullYear(), bugun.getMonth() + i, 1);
        aylar.push(ayKey(d.getFullYear(), d.getMonth()));
      }
      const unsubs = [];
      aylar.forEach(ak => {
        const unsubCalisma = onSnapshot(doc(db, 'aylikCalisma', ak), snap => {
          setCalismaVerisi(prev => ({ ...prev, [ak]: snap.exists() ? snap.data().kayitlar : {} }));
        });
        const unsubAvans = onSnapshot(doc(db, 'aylikAvans', ak), snap => {
          setAvansVerisi(prev => ({ ...prev, [ak]: snap.exists() ? snap.data() : {} }));
        });
        unsubs.push(unsubCalisma, unsubAvans);
      });
      return () => unsubs.forEach(u => u());
    }, []);

    useEffect(() => {
      const unsubOdemeler = onSnapshot(
        query(collection(db, 'odemeler'), orderBy('tarih', 'desc')),
        snap => setOdemeler(snap.docs.map(d => ({ id: d.id, ...d.data() })))
      );
      return () => unsubOdemeler();
    }, []);

    // ─── YARDIMCI FONKSIYONLAR ──────────────────────────────────────────────
    const getSantiyeAd = useCallback((sId) => {
      return santiyeler.find(s => s.id === sId)?.ad || 'Bilinmeyen';
    }, [santiyeler]);

    const hesaplaUstaDetay = useCallback((uId, yovmiye) => {
      const aylikOzet = {};
      const tumAylar = [];

      Object.entries(calismaVerisi).forEach(([ak, kayitlar]) => {
        if (!kayitlar) return;
        let gunSayisi = 0;
        const gunDetaylari = [];
        Object.entries(kayitlar).forEach(([gunKey, ustaMap]) => {
          if (ustaMap && ustaMap[uId]) {
            gunSayisi++;
            gunDetaylari.push({ gun: gunKey, santiyeId: ustaMap[uId], santiyeAd: getSantiyeAd(ustaMap[uId]) });
          }
        });
        if (gunSayisi > 0 || avansVerisi[ak]?.[uId]) {
          const hakedis = gunSayisi * Number(yovmiye || 0);
          aylikOzet[ak] = { gunSayisi, hakedis, avans: 0, odeme: 0, gunDetaylari, kalanBorc: hakedis };
          if (!tumAylar.includes(ak)) tumAylar.push(ak);
        }
      });

      Object.entries(avansVerisi).forEach(([ak, ustaMap]) => {
        const ustaVerisi = ustaMap?.[uId];
        if (!ustaVerisi) return;
        if (!aylikOzet[ak]) {
          aylikOzet[ak] = { gunSayisi: 0, hakedis: 0, avans: 0, odeme: 0, gunDetaylari: [], kalanBorc: 0 };
          if (!tumAylar.includes(ak)) tumAylar.push(ak);
        }
        if (typeof ustaVerisi === 'object') {
          Object.values(ustaVerisi).forEach(tutar => { aylikOzet[ak].avans += Number(tutar || 0); });
        } else if (typeof ustaVerisi === 'number') {
          aylikOzet[ak].avans += Number(ustaVerisi || 0);
        }
      });

      odemeler.filter(o => o.ustaId === uId).forEach(o => {
        const odemeTutari = Number(o.tutar || 0);
        const hedefAy = o.ilgiliAy || (() => {
          const tarih = o.tarih?.toDate ? o.tarih.toDate() : new Date(o.tarih);
          return ayKey(tarih.getFullYear(), tarih.getMonth());
        })();
        if (!aylikOzet[hedefAy]) {
          aylikOzet[hedefAy] = { gunSayisi: 0, hakedis: 0, avans: 0, odeme: 0, gunDetaylari: [], kalanBorc: 0 };
          if (!tumAylar.includes(hedefAy)) tumAylar.push(hedefAy);
        }
        aylikOzet[hedefAy].odeme += odemeTutari;
      });

      tumAylar.sort();
      let yuruyenBakiye = 0;
      const aylikOzetSirali = {};
      tumAylar.forEach(ak => {
        const o = aylikOzet[ak];
        o.kalanBorc = o.hakedis - o.avans - o.odeme;
        yuruyenBakiye += o.kalanBorc;
        o.yuruyenBakiye = yuruyenBakiye;
        aylikOzetSirali[ak] = o;
      });

      const toplamHakedis = tumAylar.reduce((t, ak) => t + aylikOzetSirali[ak].hakedis, 0);
      const toplamAvans = tumAylar.reduce((t, ak) => t + aylikOzetSirali[ak].avans, 0);
      const toplamOdeme = tumAylar.reduce((t, ak) => t + aylikOzetSirali[ak].odeme, 0);

      return { toplamHakedis, toplamAvans, toplamOdeme, netBakiye: yuruyenBakiye, aylikOzet: aylikOzetSirali, tumAylar, borcluAylar: tumAylar.filter(ak => aylikOzetSirali[ak].kalanBorc > 0) };
    }, [calismaVerisi, avansVerisi, odemeler, getSantiyeAd]);

    const hesaplaFIFODagilim = useCallback((uId, yovmiye, odemeTutari) => {
      const detay = hesaplaUstaDetay(uId, yovmiye);
      const dagilim = [];
      let kalanOdeme = odemeTutari;
      for (const ak of detay.tumAylar) {
        if (kalanOdeme <= 0) break;
        const ayBorc = detay.aylikOzet[ak].kalanBorc;
        if (ayBorc > 0) {
          const kapanan = Math.min(ayBorc, kalanOdeme);
          dagilim.push({ ay: ak, ayBorc, kapanan, kalanBorc: ayBorc - kapanan, tamamenKapandi: kapanan >= ayBorc });
          kalanOdeme -= kapanan;
        }
      }
      return { dagilim, kalanOdeme, toplamKapanan: odemeTutari - kalanOdeme, detay };
    }, [hesaplaUstaDetay]);

    const genelOzet = useMemo(() => {
      let toplamBorc = 0, toplamAvansVerilen = 0, toplamOdenen = 0, calisanSayisi = 0, borcluSayisi = 0;
      ustalar.forEach(u => {
        const { netBakiye, toplamAvans, toplamOdeme, toplamHakedis } = hesaplaUstaDetay(u.id, u.yovmiye);
        if (netBakiye > 0) { toplamBorc += netBakiye; borcluSayisi++; }
        toplamAvansVerilen += toplamAvans;
        toplamOdenen += toplamOdeme;
        if (toplamHakedis > 0) calisanSayisi++;
      });
      return { toplamBorc, toplamAvansVerilen, toplamOdenen, calisanSayisi, borcluSayisi };
    }, [ustalar, hesaplaUstaDetay]);

    const calisanListesi = useMemo(() => {
      return ustalar.map(u => { const detay = hesaplaUstaDetay(u.id, u.yovmiye); return { ...u, ...detay }; });
    }, [ustalar, hesaplaUstaDetay]);

    const filtreliListe = useMemo(() => {
      let liste = calisanListesi.filter(c => {
        if (!aramaMetni) return true;
        return c.ad?.toLowerCase().includes(aramaMetni.toLowerCase());
      });
      liste.sort((a, b) => {
        if (siralama === 'borcAzalan') return b.netBakiye - a.netBakiye;
        if (siralama === 'borcArtan') return a.netBakiye - b.netBakiye;
        if (siralama === 'adaGore') return (a.ad || '').localeCompare(b.ad || '');
        return 0;
      });
      return liste;
    }, [calisanListesi, aramaMetni, siralama]);

    const odemeKaydet = async () => {
      if (!seciliUsta || !odemeTutari || Number(odemeTutari) <= 0) return;
      if (odemeYontemi === 'havale' && !seciliIban) { alert('Lutfen gonderici IBAN bilgisini secin.'); return; }
      if (odemeYontemi === 'havale' && !havaleAdSoyad.trim()) { alert('Lutfen havaleyi yapan kisinin adini girin.'); return; }
      if (odemeYontemi === 'cek' && !cekNo.trim()) { alert('Lutfen cek numarasini girin.'); return; }

      setYukleniyor(true);
      try {
        const tutar = Number(odemeTutari);
        const [y, m, d] = odemeTarihi.split('-').map(Number);
        const ak = ayKey(y, m - 1);
        const { dagilim, kalanOdeme } = hesaplaFIFODagilim(seciliUsta.id, seciliUsta.yovmiye, tutar);

        const batchOdemeler = [];
        for (const d of dagilim) {
          if (d.kapanan > 0) {
            const odemeData = {
              ustaId: seciliUsta.id, tarih: new Date(odemeTarihi), tutar: d.kapanan,
              aciklama: `${odemeAciklama || 'Odeme'} - ${d.ay} borc kapanisi`,
              ilgiliAy: d.ay, odemeYontemi, kesimNoktasi: d.tamamenKapandi, olusturmaTarihi: serverTimestamp()
            };
            if (odemeYontemi === 'havale') {
              const iban = ibanListesi.find(i => i.id === seciliIban);
              odemeData.ibanBilgisi = { iban: iban?.iban || seciliIban, hesapSahibi: havaleAdSoyad.trim(), banka: havaleBanka.trim() || iban?.banka || '' };
            }
            if (odemeYontemi === 'cek') { odemeData.cekNo = cekNo.trim(); odemeData.cekTarihi = cekTarihi; }
            batchOdemeler.push(odemeData);
          }
        }

        if (kalanOdeme > 0.01) {
          const odemeData = {
            ustaId: seciliUsta.id, tarih: new Date(odemeTarihi), tutar: kalanOdeme,
            aciklama: `${odemeAciklama || 'Odeme'} - Fazla odeme / Avans`,
            ilgiliAy: ak, odemeYontemi, kesimNoktasi: false, fazlaOdeme: true, olusturmaTarihi: serverTimestamp()
          };
          if (odemeYontemi === 'havale') {
            const iban = ibanListesi.find(i => i.id === seciliIban);
            odemeData.ibanBilgisi = { iban: iban?.iban || seciliIban, hesapSahibi: havaleAdSoyad.trim(), banka: havaleBanka.trim() || iban?.banka || '' };
          }
          if (odemeYontemi === 'cek') { odemeData.cekNo = cekNo.trim(); odemeData.cekTarihi = cekTarihi; }
          batchOdemeler.push(odemeData);
        }

        for (const odemeData of batchOdemeler) { await setDoc(doc(collection(db, 'odemeler')), odemeData); }

        setOdemeModalAcik(false); setOdemeTutari(''); setOdemeAciklama(''); setSeciliIban('');
        setHavaleAdSoyad(''); setHavaleBanka(''); setCekNo(''); setCekTarihi(bugun.toISOString().split('T')[0]);
      } catch (err) { alert('Odeme kaydedilemedi: ' + err.message); }
      finally { setYukleniyor(false); }
    };

    // ─── ÖDEME SİL ──────────────────────────────────────────────────────────
    const odemeSil = async () => {
      if (!silinecekOdeme) return;
      try { await deleteDoc(doc(db, 'odemeler', silinecekOdeme.id)); setSilmeModalAcik(false); setSilinecekOdeme(null); }
      catch (err) { alert('Silme hatasi: ' + err.message); }
    };

    // ─── RENDER ─────────────────────────────────────────────────────────────
    return (
      <div className="min-h-screen bg-stone-950 pb-32">
        {/* ═══ HEADER ═══════════════════════════════════════════════════════ */}
        <div className="sticky top-0 z-40 bg-stone-950/80 backdrop-blur-xl border-b border-stone-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">Muhasebe</h1>
                <p className="text-[10px] text-stone-500 font-bold uppercase tracking-widest mt-0.5">
                  Yürüyen Bakiye & Ödeme Yönetimi
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDetayliGorunum(!detayliGorunum)}
                  className={`p-2.5 rounded-xl transition-all ${detayliGorunum ? 'bg-white text-stone-900' : 'bg-stone-900 text-stone-400 border border-stone-800'}`}
                  title={detayliGorunum ? 'Basit görünüm' : 'Detaylı görünüm'}
                >
                  {detayliGorunum ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4 pt-4">

          {/* ═══ ÖZET KARTLARI ════════════════════════════════════════════════ */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <OzetKart
              label="Toplam Borç"
              deger={genelOzet.toplamBorc}
              icon={Receipt}
              renk="text-red-400"
              bg="bg-red-400/5"
              border="border-red-400/10"
              alt="Ödeme bekleyen"
            />
            <OzetKart
              label="Verilen Avans"
              deger={genelOzet.toplamAvansVerilen}
              icon={PiggyBank}
              renk="text-yellow-500"
              bg="bg-yellow-500/5"
              border="border-yellow-500/10"
              alt="Bugüne kadar"
            />
            <OzetKart
              label="Yapılan Ödeme"
              deger={genelOzet.toplamOdenen}
              icon={CreditCard}
              renk="text-blue-400"
              bg="bg-blue-400/5"
              border="border-blue-400/10"
              alt="Hesap kapatma"
            />
            <OzetKart
              label="Borçlu Çalışan"
              deger={genelOzet.borcluSayisi}
              icon={Users}
              renk={genelOzet.borcluSayisi > 0 ? 'text-red-400' : 'text-green-400'}
              bg={genelOzet.borcluSayisi > 0 ? 'bg-red-400/5' : 'bg-green-400/5'}
              border={genelOzet.borcluSayisi > 0 ? 'border-red-400/10' : 'border-green-400/10'}
              alt={`${genelOzet.calisanSayisi} toplam çalışan`}
              paraFormati={false}
            />
          </div>

          {/* ═══ ARAMA & FİLTRELER ══════════════════════════════════════════ */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                type="text"
                value={aramaMetni}
                onChange={e => setAramaMetni(e.target.value)}
                placeholder="Çalışan ara..."
                className="w-full bg-stone-900 border border-stone-800 rounded-xl pl-10 pr-4 py-3 text-sm text-white font-bold outline-none focus:border-stone-600 placeholder-stone-600"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={siralama}
                onChange={e => setSiralama(e.target.value)}
                className="bg-stone-900 border border-stone-800 rounded-xl px-4 py-3 text-xs font-bold text-stone-300 outline-none focus:border-stone-600"
              >
                <option value="borcAzalan">En Yüksek Borç</option>
                <option value="borcArtan">En Düşük Borç</option>
                <option value="adaGore">İsim (A-Z)</option>
              </select>
            </div>
          </div>

          {/* ═══ ÇALIŞAN KARTLARI ═══════════════════════════════════════════ */}
          <div className="space-y-3">
            {filtreliListe.length === 0 && (
              <div className="text-center py-16 text-stone-600">
                <Users size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-bold">Sonuç bulunamadı</p>
              </div>
            )}

            {filtreliListe.map(u => {
              const odendiMi = u.netBakiye <= 0 && u.toplamHakedis > 0;
              const borcluMu = u.netBakiye > 0;
              const sonBorcluAylar = u.borcluAylar.slice(-3); // Son 3 borçlu ay

              return (
                <div
                  key={u.id}
                  className={`bg-stone-900 border rounded-2xl overflow-hidden transition-all ${borcluMu ? 'border-red-500/15' : odendiMi ? 'border-green-500/15' : 'border-stone-800'
                    }`}
                >
                  {/* Ana Kart */}
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start gap-3 sm:gap-4">
                      {/* Avatar */}
                      <div
                        onClick={() => setSeciliUstaDetay(seciliUstaDetay === u.id ? null : u.id)}
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl border flex items-center justify-center font-black text-lg flex-shrink-0 cursor-pointer transition-all relative ${odendiMi
                            ? 'bg-green-500/10 border-green-500/20 text-green-400'
                            : borcluMu
                              ? 'bg-red-500/10 border-red-500/20 text-red-400'
                              : 'bg-stone-800 border-stone-700 text-stone-500'
                          }`}
                      >
                        {u.ad?.[0]?.toUpperCase() || '?'}
                        {odendiMi && <CheckCircle2 size={14} className="absolute -bottom-0.5 -right-0.5 text-green-400 bg-stone-950 rounded-full" />}
                      </div>

                      {/* Bilgiler */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm sm:text-base font-bold text-white truncate">{u.ad}</h3>
                          {odendiMi && (
                            <span className="text-[9px] font-black bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full border border-green-500/20">
                              TEMİZ
                            </span>
                          )}
                          {borcluMu && u.borcluAylar.length > 0 && (
                            <span className="text-[9px] font-black bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full border border-red-500/20">
                              {u.borcluAylar.length} AY BORÇLU
                            </span>
                          )}
                        </div>

                        {/* Hızlı Özet */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] sm:text-xs">
                          <span className="text-stone-500 font-bold">
                            Yevmiye: <span className="text-stone-300">{paraCevir(u.yovmiye)}</span>
                          </span>
                          <span className="text-stone-500 font-bold">
                            Hakediş: <span className="text-green-400">{paraCevir(u.toplamHakedis)}</span>
                          </span>
                          <span className="text-stone-500 font-bold">
                            Avans: <span className="text-yellow-500">{paraCevir(u.toplamAvans)}</span>
                          </span>
                        </div>

                        {/* Son Borçlu Aylar */}
                        {sonBorcluAylar.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {sonBorcluAylar.map(ak => {
                              const veri = u.aylikOzet[ak];
                              const net = veri.kalanBorc;
                              return (
                                <span
                                  key={ak}
                                  className="text-[9px] font-bold bg-stone-800 text-stone-400 px-2 py-1 rounded-lg border border-stone-700/30"
                                >
                                  {ak}: {paraCevir(net)}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Sağ: Bakiye & Buton */}
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <div className="text-right">
                          <div className="text-[9px] text-stone-600 font-black uppercase">Net</div>
                          <div className={`text-lg sm:text-xl font-black ${borcluMu ? 'text-red-400' : 'text-green-400'}`}>
                            {paraCevir(Math.abs(u.netBakiye))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSeciliUsta(u);
                              setOdemeTutari(String(Math.max(0, u.netBakiye)));
                              setOdemeAciklama(`${u.ad} — Hesap Ödemesi`);
                              setOdemeModalAcik(true);
                            }}
                            disabled={u.netBakiye <= 0}
                            className="px-4 py-2 bg-white text-stone-900 rounded-xl text-[10px] font-black hover:bg-stone-200 transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            ÖDE
                          </button>
                          <button
                            onClick={() => setSeciliUstaDetay(seciliUstaDetay === u.id ? null : u.id)}
                            className="p-2 bg-stone-800 rounded-xl text-stone-400 hover:text-white transition-all"
                          >
                            {seciliUstaDetay === u.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detay Paneli (Açılır) */}
                  {seciliUstaDetay === u.id && detayliGorunum && (
                    <div className="border-t border-stone-800/50 bg-stone-900/50 px-4 sm:px-5 py-4">
                      <h4 className="text-[10px] font-black text-stone-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <History size={12} /> Aylık Detay (Eskiden Yeniye)
                      </h4>

                      <div className="space-y-2">
                        {u.tumAylar.map(ak => {
                          const veri = u.aylikOzet[ak];
                          const net = veri.kalanBorc;
                          const ayOdendi = veri.hakedis > 0 && net <= 0;
                          return (
                            <div
                              key={ak}
                              className={`flex items-center justify-between p-3 rounded-xl border ${net > 0
                                  ? 'bg-red-500/5 border-red-500/10'
                                  : ayOdendi
                                    ? 'bg-green-500/5 border-green-500/10'
                                    : 'bg-stone-800/30 border-stone-800'
                                }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-black text-stone-400 w-16">{ak}</span>
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px]">
                                  <span className="text-stone-500">
                                    {veri.gunSayisi} gün
                                  </span>
                                  <span className="text-green-400 font-bold">
                                    +{paraCevir(veri.hakedis)}
                                  </span>
                                  {veri.avans > 0 && (
                                    <span className="text-yellow-500 font-bold">
                                      -{paraCevir(veri.avans)}
                                    </span>
                                  )}
                                  {veri.odeme > 0 && (
                                    <span className="text-blue-400 font-bold">
                                      -{paraCevir(veri.odeme)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className={`text-xs font-black ${net > 0 ? 'text-red-400' : ayOdendi ? 'text-green-400' : 'text-stone-500'
                                }`}>
                                {net > 0 ? `${paraCevir(net)} BORÇ` : ayOdendi ? '✓ KAPANDI' : '—'}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Ödeme Geçmişi */}
                      <h4 className="text-[10px] font-black text-stone-500 uppercase tracking-widest mt-4 mb-3 flex items-center gap-1.5">
                        <CreditCard size={12} /> Ödeme Geçmişi
                      </h4>
                      <div className="space-y-1.5">
                        {odemeler
                          .filter(o => o.ustaId === u.id)
                          .sort((a, b) => {
                            const ta = a.tarih?.toDate ? a.tarih.toDate() : new Date(a.tarih);
                            const tb = b.tarih?.toDate ? b.tarih.toDate() : new Date(b.tarih);
                            return tb - ta;
                          })
                          .map(o => {
                            const tarih = o.tarih?.toDate ? o.tarih.toDate() : new Date(o.tarih);
                            return (
                              <div
                                key={o.id}
                                className="flex items-center justify-between p-2.5 rounded-xl bg-stone-800/30 border border-stone-800"
                              >
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 size={12} className="text-green-400" />
                                  <div>
                                    <div className="text-xs font-bold text-white">{paraCevir(o.tutar)}</div>
                                    <div className="text-[9px] text-stone-500">
                                      {tarih.toLocaleDateString('tr-TR')} • {o.ilgiliAy} • {o.odemeYontemi || 'Nakit'}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] text-stone-500 truncate max-w-[120px]">
                                    {o.aciklama}
                                  </span>
                                  <button
                                    onClick={() => { setSilinecekOdeme(o); setSilmeModalAcik(true); }}
                                    className="p-1.5 text-stone-600 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                  {/* PDF INDIR BUTONU */}
                                  <button
                                    onClick={() => makbuzOlustur(o, u, u.aylikOzet)}
                                    title="PDF Makbuz Indir"
                                    className="p-1.5 text-stone-500 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-all"
                                  >
                                    <Download size={13} />
                                  </button>

                                </div>
                              </div>
                            );
                          })}
                        {odemeler.filter(o => o.ustaId === u.id).length === 0 && (
                          <p className="text-[10px] text-stone-600 text-center py-2">Henüz ödeme kaydı yok</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══ ÖDEME MODALİ ═════════════════════════════════════════════════ */}
        
        {/* Havale DETAYLARI */}
        {odemeYontemi === 'havale' && (
          <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4 space-y-3">
            <div className="text-[10px] text-blue-400 font-black uppercase tracking-widest mb-1">Havale/EFT Detaylari</div>
            <div>
              <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Gonderici IBAN *</label>
              <select
                value={seciliIban}
                onChange={e => {
                  setSeciliIban(e.target.value);
                  const iban = ibanListesi.find(i => i.id === e.target.value);
                  if (iban) setHavaleBanka(iban.banka);
                }}
                className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-blue-500"
              >
                <option value="">--- IBAN Secin ---</option>
                {ibanListesi.map(i => (
                  <option key={i.id} value={i.id}>{i.banka} - {i.iban}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Havaleyi Yapan Kisi *</label>
              <input type="text" value={havaleAdSoyad} onChange={e => setHavaleAdSoyad(e.target.value)}
                placeholder="Ad Soyad" className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Banka</label>
              <input type="text" value={havaleBanka} onChange={e => setHavaleBanka(e.target.value)}
                placeholder="Banka adi" className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-blue-500" />
            </div>
            {/* YENİ EKLENEN: ALICI IBAN */}

          </div>
        )}

        {/* CEK DETAYLARI */}
        {odemeYontemi === 'cek' && (
          <div className="bg-yellow-500/5 border border-yellow-500/15 rounded-xl p-4 space-y-3">
            <div className="text-[10px] text-yellow-500 font-black uppercase tracking-widest mb-1">Cek Detaylari</div>
            <div>
              <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Cek Numarasi *</label>
              <input type="text" value={cekNo} onChange={e => setCekNo(e.target.value)}
                placeholder="Cek no" className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-yellow-500" />
            </div>
            <div>
              <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Cek Tarihi</label>
              <input type="date" value={cekTarihi} onChange={e => setCekTarihi(e.target.value)}
                className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-yellow-500" />
            </div>
          </div>
        )}
        {odemeModalAcik && seciliUsta && (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/90 backdrop-blur-md">
            <div className="bg-stone-900 w-full max-w-lg rounded-t-[2rem] sm:rounded-[2rem] max-h-[95vh] overflow-hidden flex flex-col border border-stone-800 shadow-2xl">
              {/* Modal Header */}
              <div className="p-5 sm:p-6 border-b border-stone-800 flex justify-between items-center bg-stone-900 sticky top-0 z-10">
                <div>
                  <h3 className="text-lg sm:text-xl font-black text-white">{seciliUsta.ad}</h3>
                  <p className="text-[10px] text-stone-500 font-black uppercase tracking-[0.2em]">Ödeme / Hesap Kesim</p>
                </div>
                <button
                  onClick={() => setOdemeModalAcik(false)}
                  className="p-2.5 bg-stone-800 rounded-xl text-stone-400 hover:text-white transition-all"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
                {/* Mevcut Durum */}
                <div className="bg-stone-800/30 rounded-2xl p-4 border border-stone-700/50 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-stone-500 font-bold">Toplam Hakediş:</span>
                    <span className="text-green-400 font-black">{paraCevir(seciliUsta.toplamHakedis)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-stone-500 font-bold">Toplam Avans:</span>
                    <span className="text-yellow-500 font-black">{paraCevir(seciliUsta.toplamAvans)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-stone-500 font-bold">Toplam Ödenen:</span>
                    <span className="text-blue-400 font-black">{paraCevir(seciliUsta.toplamOdeme)}</span>
                  </div>
                  <div className="border-t border-stone-700 pt-2 flex justify-between text-sm">
                    <span className="text-white font-bold">Net Bakiye:</span>
                    <span className={`font-black ${seciliUsta.netBakiye > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {paraCevir(Math.abs(seciliUsta.netBakiye))}
                    </span>
                  </div>
                </div>

                {/* FIFO Önizleme */}
                {odemeTutari && Number(odemeTutari) > 0 && (
                  <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-2xl p-4">
                    <div className="text-[10px] text-emerald-400 font-black uppercase tracking-widest mb-3 flex items-center gap-1.5">
                      <ArrowDown size={12} /> FIFO Dağılım Önizlemesi
                    </div>
                    {(() => {
                      const { dagilim, kalanOdeme } = hesaplaFIFODagilim(
                        seciliUsta.id, seciliUsta.yovmiye, Number(odemeTutari)
                      );
                      return (
                        <div className="space-y-2">
                          {dagilim.map((d, i) => (
                            <div key={i} className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center text-[10px] font-black">
                                  {i + 1}
                                </span>
                                <span className="text-stone-300 font-bold">{d.ay}</span>
                                <span className="text-stone-500 text-[10px]">
                                  ({paraCevir(d.ayBorc)} borç)
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-emerald-400 font-black">-{paraCevir(d.kapanan)}</span>
                                {d.tamamenKapandi && <CheckCircle2 size={12} className="text-emerald-400" />}
                              </div>
                            </div>
                          ))}
                          {kalanOdeme > 0.01 && (
                            <div className="flex items-center justify-between text-xs pt-2 border-t border-emerald-500/10">
                              <span className="text-yellow-400 font-bold">Fazla Ödeme / Avans</span>
                              <span className="text-yellow-400 font-black">+{paraCevir(kalanOdeme)}</span>
                            </div>
                          )}
                          {dagilim.length === 0 && (
                            <p className="text-[10px] text-stone-500 text-center">Tüm borçlar kapalı, ödeme avans olarak kaydedilecek</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Borçlu Aylar */}
                {seciliUsta.borcluAylar.length > 0 && (
                  <div className="bg-red-500/5 border border-red-500/15 rounded-xl p-3">
                    <div className="text-[10px] text-red-400 font-black uppercase tracking-widest mb-2">Borçlu Olunan Aylar</div>
                    <div className="flex flex-wrap gap-1.5">
                      {seciliUsta.borcluAylar.map(ak => {
                        const veri = seciliUsta.aylikOzet[ak];
                        return (
                          <span key={ak} className="text-[9px] font-bold bg-red-500/10 text-red-400 px-2 py-1 rounded-lg border border-red-500/20">
                            {ak}: {paraCevir(veri.kalanBorc)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Ödeme Tutarı</label>
                    <input
                      type="number"
                      value={odemeTutari}
                      onChange={e => setOdemeTutari(e.target.value)}
                      className="w-full bg-stone-800 border-2 border-stone-700 rounded-2xl p-4 text-white text-center text-2xl font-black outline-none focus:border-green-500 transition-colors"
                      placeholder="0"
                      autoFocus
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Tarih</label>
                      <input
                        type="date"
                        value={odemeTarihi}
                        onChange={e => setOdemeTarihi(e.target.value)}
                        className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-stone-600"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Yöntem</label>
                      <select
                        value={odemeYontemi}
                        onChange={e => setOdemeYontemi(e.target.value)}
                        className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-stone-600"
                      >
                        <option value="nakit">Nakit</option>
                        <option value="havale">Havale/EFT</option>
                        <option value="cek">Çek</option>
                      </select>
                    </div>
                  </div>
                  {/* HavalE DETAYLARI */}
                  {odemeYontemi === 'havale' && (
                    <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4 space-y-3">
                      <div className="text-[10px] text-blue-400 font-black uppercase tracking-widest mb-1">Havale/EFT Detaylari</div>
                      <div>
                        <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Gonderici IBAN *</label>
                        <select
                          value={seciliIban}
                          onChange={e => {
                            setSeciliIban(e.target.value);
                            const iban = ibanListesi.find(i => i.id === e.target.value);
                            if (iban) setHavaleBanka(iban.banka);
                          }}
                          className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-blue-500"
                        >
                          <option value="">--- IBAN Secin ---</option>
                          {ibanListesi.map(i => (
                            <option key={i.id} value={i.id}>{i.banka} - {i.iban}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Havaleyi Yapan Kisi *</label>
                        <input type="text" value={havaleAdSoyad} onChange={e => setHavaleAdSoyad(e.target.value)}
                          placeholder="Ad Soyad" className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Banka</label>
                        <input type="text" value={havaleBanka} onChange={e => setHavaleBanka(e.target.value)}
                          placeholder="Banka adi" className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-blue-500" />
                      </div>
                      <div>
      <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Alici IBAN *</label>
      <input 
        type="text" 
        value={aliciIban} 
        onChange={e => setAliciIban(e.target.value)}
        placeholder="TRXX XXXX XXXX XXXX XXXX XXXX XX" 
        className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-blue-500" 
      />
    </div>
                    </div>
                  )}

                  {/* CEK DETAYLARI */}
                  {odemeYontemi === 'cek' && (
                    <div className="bg-yellow-500/5 border border-yellow-500/15 rounded-xl p-4 space-y-3">
                      <div className="text-[10px] text-yellow-500 font-black uppercase tracking-widest mb-1">Cek Detaylari</div>
                      <div>
                        <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Cek Numarasi *</label>
                        <input type="text" value={cekNo} onChange={e => setCekNo(e.target.value)}
                          placeholder="Cek no" className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-yellow-500" />
                      </div>
                      <div>
                        <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Cek Tarihi</label>
                        <input type="date" value={cekTarihi} onChange={e => setCekTarihi(e.target.value)}
                          className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-yellow-500" />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Açıklama</label>
                    <input
                      type="text"
                      value={odemeAciklama}
                      onChange={e => setOdemeAciklama(e.target.value)}
                      className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-stone-600 placeholder-stone-600"
                      placeholder="Örn: Mayıs-Haziran borç kapatma"
                    />
                  </div>
                </div>

                <div className="bg-yellow-500/5 border border-yellow-500/15 rounded-xl p-3 flex gap-3">
                  <AlertCircle size={16} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-yellow-600 font-bold leading-relaxed">
                    Ödeme en eski borçtan başlayarak otomatik dağıtılır. Önce Mayıs, sonra Haziran şeklinde kapatılır. Fazla ödeme avans olarak kaydedilir.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setOdemeModalAcik(false)}
                    className="py-4 rounded-2xl bg-stone-800 text-stone-400 font-bold text-xs hover:bg-stone-700 transition-all"
                  >
                    İPTAL
                  </button>
                  <button
                    onClick={odemeKaydet}
                    disabled={yukleniyor || !odemeTutari || Number(odemeTutari) <= 0}
                    className="py-4 rounded-2xl bg-green-500 text-stone-900 font-black text-xs hover:bg-green-400 disabled:opacity-40 transition-all active:scale-95 flex items-center justify-center gap-2"
                  >
                    {yukleniyor ? 'KAYDEDİLİYOR...' : 'ÖDEME YAP'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ SİLME ONAY MODALİ ════════════════════════════════════════════ */}
        {silmeModalAcik && silinecekOdeme && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="bg-stone-900 w-full max-w-sm rounded-3xl p-6 border border-stone-800 shadow-2xl">
              <div className="w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
                <Trash2 size={20} className="text-red-400" />
              </div>
              <h3 className="text-lg font-black text-white text-center mb-1">Ödemeyi Sil</h3>
              <p className="text-xs text-stone-500 text-center mb-6">
                {paraCevir(silinecekOdeme.tutar)} tutarındaki ödemeyi silmek istediğinize emin misiniz?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSilmeModalAcik(false)}
                  className="py-3 rounded-xl bg-stone-800 text-stone-400 font-bold text-xs hover:bg-stone-700 transition-all"
                >
                  İPTAL
                </button>
                <button
                  onClick={odemeSil}
                  className="py-3 rounded-xl bg-red-500 text-white font-black text-xs hover:bg-red-400 transition-all active:scale-95"
                >
                  SİL
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── ALT BİLEŞENLER ───────────────────────────────────────────────────────
  function OzetKart({ label, deger, icon: Icon, renk, bg, border, alt, paraFormati = true }) {
    return (
      <div className={`${bg} border ${border} p-4 rounded-2xl`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon size={14} className={renk} />
          <span className="text-[9px] font-black uppercase tracking-wider text-stone-500">{label}</span>
        </div>
        <div className={`text-xl sm:text-2xl font-black tracking-tight ${renk}`}>
          {paraFormati ? paraCevir(deger) : deger}
        </div>
        <p className="text-[9px] text-stone-600 mt-1 font-bold">{alt}</p>
      </div>
    );
  }